"use strict";
import { useMemo, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TokenOptionSection } from "uniswap/src/components/TokenSelector/types";
import { parseUnits } from "viem";
import { useAccount, useWalletClient } from "wagmi";
import { mockLendingProposals } from "../mocks/mockProposal";
import { mockTokensBalances } from "../mocks/mockTokens";
export var SelectionModalMode = /* @__PURE__ */ ((SelectionModalMode2) => {
  SelectionModalMode2["ASSET"] = "asset";
  SelectionModalMode2["POOL"] = "pool";
  SelectionModalMode2["ASSET_2"] = "asset_2";
  return SelectionModalMode2;
})(SelectionModalMode || {});
export var ModalState = /* @__PURE__ */ ((ModalState2) => {
  ModalState2["OPEN"] = "open";
  ModalState2["CLOSE"] = "close";
  return ModalState2;
})(ModalState || {});
export var APP_TABS = /* @__PURE__ */ ((APP_TABS2) => {
  APP_TABS2["BORROW"] = "borrow";
  APP_TABS2["LEND"] = "lend";
  APP_TABS2["MY_LENDING"] = "my-lending";
  APP_TABS2["MY_BORROWING"] = "my-borrowing";
  return APP_TABS2;
})(APP_TABS || {});
export const useLendingState = () => {
  const { address } = useAccount();
  const selectionModalReducer = (state, action) => {
    switch (action.type) {
      case "open" /* OPEN */:
        return {
          isOpen: true,
          mode: action.mode
        };
      case "close" /* CLOSE */:
        return {
          isOpen: false,
          mode: null
        };
      default:
        return state;
    }
  };
  const [selectionModalState, selectionModalDispatch] = useReducer(selectionModalReducer, {
    mode: null,
    isOpen: false
  });
  const [assetInputValue, setAssetInputValue] = useState("");
  const [selectedAppTab, selectAppTab] = useState("borrow" /* BORROW */);
  const [selectedPool, changePool] = useState(null);
  const [selectedAsset, setAsset] = useState(null);
  const [selectedAsset2, changeAsset2] = useState(null);
  const [ltv, setLtv] = useState(null);
  const [interestRate, setInterestRate] = useState(null);
  const [selectedProposal, changeSelectedProposal] = useState(null);
  const [isOffersClosed, closeOffers] = useState(false);
  const parseToUppercase = (input) => {
    return String(input).toUpperCase();
  };
  const proposals = useMemo(() => {
    return mockLendingProposals.filter((p) => {
      const credit = parseUnits(assetInputValue, selectedAsset?.currency?.decimals ?? 0) ?? 0n;
      const mode = selectedAppTab === "borrow" /* BORROW */ ? "borrow" : selectedAppTab === "lend" /* LEND */ ? "lend" : "all";
      if (mode === "borrow") {
        if (parseToUppercase(selectedAsset?.currency?.address) !== parseToUppercase(p.creditAsset.address)) {
          return false;
        }
        if (interestRate) {
          return (credit > 0n ? p.creditAmount >= Number(credit) : true) && p.apr <= interestRate * 1e3;
        } else {
          return credit > 0n ? p.creditAmount >= Number(credit) : true;
        }
      } else if (mode === "lend") {
        if (parseToUppercase(selectedAsset?.currency?.address) !== parseToUppercase(p.creditAsset.address)) {
          return false;
        }
        if (interestRate) {
          return (credit > 0n ? p.creditAmount <= Number(credit) : true) && p.apr >= interestRate * 1e3;
        } else {
          return credit > 0n ? p.creditAmount <= Number(credit) : true;
        }
      } else {
        return true;
      }
    }).sort((a, b) => {
      const mode = selectedAppTab === "borrow" /* BORROW */ ? "borrow" : selectedAppTab === "lend" /* LEND */ ? "lend" : "all";
      if (a.apr !== b.apr) {
        return mode === "lend" ? b.apr - a.apr : a.apr - b.apr;
      }
      if (a.creditAmount !== b.creditAmount) {
        return Number(a.creditAmount - b.creditAmount);
      }
      return 0;
    });
  }, [assetInputValue, selectedAsset, selectedAppTab, interestRate]);
  const bestProposal = useMemo(() => {
    if (!proposals || proposals.length === 0)
      return null;
    const mode = selectedAppTab === "borrow" /* BORROW */ ? "borrow" : selectedAppTab === "lend" /* LEND */ ? "lend" : "all";
    if (mode === "lend" && !selectedAsset)
      return null;
    if (mode === "borrow" && !selectedPool)
      return null;
    return {
      [proposals[0].id]: proposals[0]
    };
  }, [proposals, selectedPool, selectedAsset, selectedAppTab]);
  const shouldShowOffers = useMemo(() => {
    const isBorrowOrLend = ["borrow" /* BORROW */, "lend" /* LEND */].includes(selectedAppTab);
    if (!isBorrowOrLend)
      return false;
    const isRespectiveSelection = selectedAppTab === "lend" /* LEND */ ? selectedAsset && assetInputValue : selectedPool && selectedAsset && assetInputValue;
    if (!isRespectiveSelection)
      return false;
    if (isOffersClosed)
      return false;
    if (!bestProposal)
      return false;
    return true;
  }, [selectedAppTab, selectedAsset, selectedPool, isOffersClosed, assetInputValue, bestProposal]);
  const changeAsset = (asset) => {
    setAsset(asset);
    const isSameAsSecondAsset = asset?.currency?.address === selectedAsset2?.currency?.address;
    if (selectedAppTab === "lend" /* LEND */ && isSameAsSecondAsset) {
      changeAsset2(null);
    }
  };
  const getAssetsByPriceFeedExists = useMemo(() => {
    return [
      {
        ...mockTokensBalances[0],
        sectionKey: TokenOptionSection.PredefinedAssets
      }
    ];
  }, []);
  const getPredefinedAssetsForSecondAsset = useMemo(() => {
    if (!selectedAsset)
      return [];
    const filtered = mockTokensBalances[0]?.data?.filter(
      (t) => t?.currencyInfo?.currency?.address !== selectedAsset?.currency?.address
    );
    return [
      {
        ...mockTokensBalances[0],
        data: filtered,
        sectionKey: TokenOptionSection.PredefinedAssets
      }
    ];
  }, [selectedAsset]);
  const getAssetsByPoolSelected = useMemo(() => {
    if (!selectedPool)
      return [];
    return [
      {
        ...mockTokensBalances[0],
        sectionKey: TokenOptionSection.PredefinedAssets
      }
    ];
  }, [selectedPool]);
  const handleResetStates = (mode) => {
    changePool(null);
    changeAsset(null);
    changeAsset2(null);
    setAssetInputValue("");
    closeOffers(false);
    if (mode === "full") {
      changeSelectedProposal(null);
    }
  };
  const { data: walletClient } = useWalletClient();
  const navigate = useNavigate();
  const handleCreateLoan = async (proposal) => {
    try {
      if (!walletClient)
        throw new Error("No wallet");
      const signature = await walletClient?.signMessage({
        message: "You will be creating the request in here, in your wallet."
      });
      console.log("signature", signature);
      handleResetStates("full");
      navigate("/borrow", { replace: true });
    } catch (error) {
      console.error("error", error);
    }
  };
  const onOpenBorrowSelectAcceptProposal = (proposal) => {
    const creditAssetObject = {
      currency: {
        ...proposal?.creditAsset
      },
      currencyId: proposal?.creditAsset.address,
      logoUrl: proposal?.creditAsset.logoUrl
    };
    closeOffers(true);
    changeAsset(creditAssetObject);
    changeSelectedProposal({
      ...proposal,
      pool: selectedPool,
      mode: "borrow"
    });
    navigate(`?accept=${proposal.id}`, { replace: false });
  };
  const onCloseBorrowSelectAcceptProposal = (proposal) => {
    changeSelectedProposal(null);
    setInterestRate(proposal?.apr);
    closeOffers(false);
    navigate(`/${selectedAppTab}`, { replace: true });
  };
  const onCloseLendSelectAcceptProposal = (proposal) => {
    changeSelectedProposal(null);
    setInterestRate(proposal?.apr);
    closeOffers(false);
    navigate(`/${selectedAppTab}`, { replace: true });
  };
  const onOpenLendSelectAcceptProposal = (proposal) => {
    const creditAssetObject = {
      currency: {
        ...proposal?.creditAsset
      },
      currencyId: proposal?.creditAsset.address,
      logoUrl: proposal?.creditAsset.logoUrl
    };
    changeAsset(creditAssetObject);
    const asset2 = {
      currency: {
        address: "0x4200000000000000000000000000000000000006",
        decimals: 18,
        isNative: false,
        isToken: true,
        symbol: "WETH",
        chainId: 8453
      },
      currencyId: "0x4200000000000000000000000000000000000006",
      logoUrl: "https://assets.coingecko.com/coins/images/2518/standard/weth.png?1696503332"
    };
    changeAsset2(asset2);
    closeOffers(true);
    changeSelectedProposal({
      ...proposal,
      mode: "lend",
      pool: selectedPool
    });
    navigate(`?accept=${proposal.id}`, { replace: false });
  };
  const handleOnDontWantToAcceptProposalResumeCustom = () => {
    const whichMode = selectedAppTab === "borrow" /* BORROW */ ? "borrow" : "lend";
    if (whichMode === "borrow") {
      onCloseBorrowSelectAcceptProposal(selectedProposal);
    } else {
      onCloseLendSelectAcceptProposal(selectedProposal);
    }
  };
  const handleOnSelectAcceptProposal = (proposal) => {
    const whichMode = selectedAppTab === "borrow" /* BORROW */ ? "borrow" : "lend";
    if (whichMode === "borrow") {
      onOpenBorrowSelectAcceptProposal(proposal);
    } else {
      onOpenLendSelectAcceptProposal(proposal);
    }
  };
  const handleDiscardAcceptProposal = () => {
    handleResetStates("full");
    navigate(`/${selectedAppTab}`, { replace: true });
  };
  const handleOnClickCloseChevron = () => {
    if (selectedProposal) {
      handleOnDontWantToAcceptProposalResumeCustom();
    } else {
      closeOffers(false);
    }
  };
  return {
    // state
    selectionModalState,
    selectedPool,
    selectedAsset,
    assetInputValue,
    selectedAppTab,
    selectedAsset2,
    ltv,
    interestRate,
    selectedProposal,
    isOffersClosed,
    proposals,
    bestProposal,
    shouldShowOffers,
    // functions
    selectionModalDispatch,
    selectAppTab,
    changePool,
    changeAsset,
    setAssetInputValue,
    changeAsset2,
    setLtv,
    setInterestRate,
    changeSelectedProposal,
    getAssetsByPoolSelected,
    getAssetsByPriceFeedExists,
    getPredefinedAssetsForSecondAsset,
    closeOffers,
    handleResetStates,
    handleCreateLoan,
    handleDiscardAcceptProposal,
    handleOnSelectAcceptProposal,
    handleOnClickCloseChevron
  };
};
